import React from 'react'

import styled from '@emotion/styled'
import {
  AlignItems,
  AlignSelf,
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  Heading2,
  Icon,
  JustifyContent,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { SocialIconShareButtons } from 'SocialLinks/SocialShareButtons'
import { SOCIAL_TYPES } from 'SocialLinks/socialReferralHelper'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import { actionTypes } from 'actions/actionTypes'

const RAFLinkPromoInput = withCitrusTheme('input', (theme) => ({
  border: `2px solid ${theme.color(Color.ColdGrey_100)}`,
  padding: `12px`,
  marginRight: `10px`,
  flex: `1`,
  borderRadius: `3px`,
}))

const RAFBoostShareContainer = styled.div`
  width: 100%;

  @media (max-width: 767px) {
    display: none;
  }
`

const CopyConfirmationText = styled.span<{ isBoost?: boolean }>`
  line-height: 24px;
  font-weight: bold;
  color: ${({ isBoost }) => (isBoost ? '#fff' : '#333D47')};
`

const ConfirmationTickIcon = styled(Icon)`
  background: #01a92b;
  border-radius: 16px;
  height: 20px;
  margin-right: 12px;
  width: 20px;
`

const RafSharingContent = styled(Box)`
  @media (max-width: 1024px) {
    flex-direction: column;
    height: auto;
  }
`

interface Props {
  userRAFLink: string
  userFirstName: string
  trackingReferFriendSocialSharing: () => void
  device: string
  trackUserFreeFoodLinkShare: ({ target }: { target: string }) => void
  trackingReferFriend: (actionType: string, actionName: string) => void
  isBoost: boolean
}

export const RAFBoostShare = ({
  userRAFLink,
  userFirstName,
  trackingReferFriendSocialSharing,
  device,
  trackUserFreeFoodLinkShare,
  trackingReferFriend,
  isBoost,
}: Props) => {
  const [copied, setCopied] = React.useState<boolean>(false)
  const onCopy = () => {
    setCopied(true)
    trackingReferFriend(actionTypes.REFER_FRIEND_LINK_COPIED, 'ReferFriendLink Copied')
    trackUserFreeFoodLinkShare({ target: SOCIAL_TYPES.link })
  }

  return (
    <RAFBoostShareContainer>
      <Box paddingBottom={5} display={Display.Flex} justifyContent={JustifyContent.Center}>
        <Heading2 color={isBoost ? Color.White : undefined}>Share your referral code</Heading2>
      </Box>
      <RafSharingContent
        paddingBottom={2}
        width="100%"
        alignItems={AlignItems.Stretch}
        display={Display.Flex}
        flexDirection={FlexDirection.Row}
      >
        <Box display={Display.Flex} width="100%" flexDirection={FlexDirection.Column} gap={2}>
          <Box display={Display.Flex} width="100%" height="48px">
            <RAFLinkPromoInput type="text" value={userRAFLink} readOnly />
            <CopyToClipboard text={userRAFLink} onCopy={() => onCopy()}>
              <Button colorVariant={ButtonColorVariant.Primary} data-testid="rafLinkCopyBtn">
                Copy link
              </Button>
            </CopyToClipboard>
          </Box>
          {copied && (
            <Box display={Display.Flex}>
              <ConfirmationTickIcon name="tick" />
              <CopyConfirmationText>Your link has been copied</CopyConfirmationText>
            </Box>
          )}
        </Box>
        <Box
          height="48px"
          display={Display.Flex}
          alignSelf={[null, null, AlignSelf.Center, AlignSelf.Stretch]}
        >
          <SocialIconShareButtons
            referralCode={userRAFLink}
            userFirstName={userFirstName}
            trackingReferFriendSocialSharing={trackingReferFriendSocialSharing}
            device={device}
            trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
          />
        </Box>
      </RafSharingContent>
    </RAFBoostShareContainer>
  )
}
